import * as imageConversion from 'image-conversion'
import constant from '@/constant'
import localStorage from '@/utils/localStorage'
import utils from './utils'
import html2canvas from 'html2canvas'

const { getLocalStorage } = localStorage
const {
  product: { IMAGE_DEFAULT, THUMB_SIZE },
} = constant
const { concatenateImageUrl, to } = utils
/**
 * url是否为绝对路径
 *
 * @param {*} url
 * @returns
 */
function isAbsoluteUrl(url: string) {
  return /^https?:\/\//i.test(url)
}

/**
 * [formatImgList 格式化货品图片集合，如果只有视频则截取视频帧并按照 size 裁剪, 货品图集则按照原尺寸返回以供显示大图用]
 * @param {string[]}  imageList [description]
 * @param {string}    video     [description]
 * @param {number = 480}         size [description]
 */
function formatImgList(
  imageList: string[],
  video: string | null = null,
  size: number = THUMB_SIZE,
) {
  // 处理主图
  if (!imageList || !imageList.length) {
    return [
      video
        ? getVideoSnapshot(`${getLocalStorage('ossRoot')}/${video}`, size)
        : IMAGE_DEFAULT,
    ]
  }

  return imageList.map((image: string) => {
    if (/https?:\/\//i.test(image)) {
      return image
    }

    if (image.includes(IMAGE_DEFAULT)) {
      return image
    }

    return `${getLocalStorage('ossRoot')}/${image}`
  })
}

/**
 * [setSimpleUrl 设置列表显示的缩略图]
 * @param {string[]} imageList [货品图集]
 */
function getListSimpleUrl(imageList: string[], size: number = THUMB_SIZE) {
  if (
    imageList[0].indexOf(IMAGE_DEFAULT) > -1 ||
    imageList[0].indexOf('video') > -1
  ) {
    return imageList[0]
  }

  return `${imageList[0]}_${size}`
}

/**
 * 获取图片的缩略图
 * @param url
 * @param size
 * @returns
 */
function getSimpleUrl(url: string, size: number = THUMB_SIZE) {
  if (!url || !url.includes('kuaipi')) return url
  return `${url}_${size}`
}

/**
 * 获取url的绝对路径
 * @param url
 * @returns
 */
function getAbsoluteUrl(url: string, size?: number) {
  if (!url) return ''

  const isAbsoluteUrl = url.startsWith('http')
  let absoluteUrl = isAbsoluteUrl ? url : `${getLocalStorage('ossRoot')}/${url}`
  if (size && !isAbsoluteUrl) absoluteUrl += `_${size}`
  return absoluteUrl
}

/**
 * 根据逗号分割的url字符串，获取url绝对路径的数组
 * @param urlStrings
 * @returns
 */
function getAbsoluteUrlByStrings(urlStrings: string, size?: number) {
  if (!urlStrings) return ''
  const urls = urlStrings.split(',').filter(Boolean)
  return urls.map((url) => getAbsoluteUrl(url, size))
}

/**
 * 视频截帧-原始尺寸
 * @param {string} url
 * @param {number} size
 * @returns {string}
 */
function getVideoOriginSnapshot(url: any) {
  return `${url}?x-oss-process=video/snapshot,t_1000,ar_auto,f_jpg,m_fast`
}

/**
 * 视频截帧
 * @param {string} url
 * @param {number} size
 * @returns {string}
 */
function getVideoSnapshot(url: string, size = 176, height = 0) {
  // 如果指定了高度就拿高度，否则取size
  const vHeight = height || size
  const imgUrl = `${url}?x-oss-process=video/snapshot,t_1000,ar_auto,f_jpg,w_${size},h_${vHeight},m_fast`

  return url.indexOf(getLocalStorage('ossRoot')) > -1
    ? imgUrl
    : `${getLocalStorage('ossRoot')}/${imgUrl}`
}

/**
 * 获取图片的宽高等信息
 * @param url
 * @returns
 */
async function getImageInfo(url: string): Promise<{
  width: number
  height: number
  naturalWidth: number
  naturalHeight: number
}> {
  if (!url || typeof url !== 'string') return Promise.reject()

  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      resolve({
        width: img.width,
        height: img.height,
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight,
      })
    }
    img.onerror = () => {
      reject()
    }
    img.src = url
  })
}

/**
 * 下载文件
 * @param url
 * @param filename
 */
function downloadFile(url: string, filename: string = 'video.mp4') {
  return fetch(url)
    .then((response) => response.blob())
    .then((blob) => {
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = filename
      link.click()
      URL.revokeObjectURL(link.href)
    })
    .catch(console.error)
}

/**
 * 图片处理(也可以拿视频绝对地址,不传size)
 * @param {string} imgUrl
 * @param {number} imgSize 图片或视频的宽高,取视频时传null(取视频首帧图片可以传size)
 * @param {boolean} isGetVideoSnapshot 是否获取视频截图,默认是
 * @returns {string}
 */
async function imgFormatter(
  imgUrl: string | null,
  imgSize?: number,
  isGetVideoSnapshot: boolean = true,
) {
  // 图片地址不存在则返回默认图片
  if (!imgUrl) {
    return IMAGE_DEFAULT
  }

  if (imgUrl.includes(IMAGE_DEFAULT)) {
    return imgUrl
  }

  // 视频截帧
  if (imgUrl.includes('video') && isGetVideoSnapshot) {
    return getVideoSnapshot(imgUrl, imgSize)
  }

  // 如果是绝对地址则直接返回
  if (
    /^(https?|cloud):\/\//i.test(imgUrl) &&
    !imgUrl.includes(getLocalStorage('ossRoot'))
  ) {
    return imgUrl
  }

  const { productImageWatermark } = { productImageWatermark: '' } //从 store.data 获取数据
  let noWatermarkImgUrl

  // 绝对地址
  if (/^(https?|cloud):\/\//i.test(imgUrl)) {
    if (!imgUrl.includes(getLocalStorage('ossRoot'))) {
      return imgUrl
    }

    noWatermarkImgUrl = `${imgUrl}${
      imgSize
        ? `?x-oss-process=image/resize,m_fill,h_${imgSize},w_${imgSize}`
        : ''
    }`
  } else {
    // 相对地址
    noWatermarkImgUrl = concatenateImageUrl(
      `${imgUrl}${
        imgSize
          ? `?x-oss-process=image/resize,m_fill,h_${imgSize},w_${imgSize}`
          : ''
      }`,
    )
  }

  if (!productImageWatermark) {
    return noWatermarkImgUrl
  }

  // 店铺开启水印时，添加水印 暂时不开发
  // const [err, watermarkImgUrl] = await to(
  //   addWatermark(imgUrl, productImageWatermark, imgSize),
  // )

  // if (err) {
  //   return noWatermarkImgUrl
  // }

  // return watermarkImgUrl
}

/**
 * 读取图片链接并转为base64
 * @param url 图片路径
 * @param ext 图片格式
 * @param width 图片宽度 和 height 都传0的时候取图片宽度
 * @param height 图片高度 和 width 都传0的时候取图片高度
 */
const getUrlBase64 = (
  url: string,
  options = { ext: 'png', width: 100, height: 100 },
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const { ext, width, height } = options
    let canvas: any = document.createElement('canvas') //创建canvas DOM元素
    const ctx: any = canvas.getContext('2d')
    const img = new Image()
    img.crossOrigin = 'Anonymous'
    img.src = url
    img.onload = function () {
      const dw = width === 0 ? img.width : width
      const dh = height === 0 ? img.height : height
      canvas.height = dh //指定画板的高度,自定义
      canvas.width = dw //指定画板的宽度，自定义
      ctx.drawImage(img, 0, 0, dw, dh) //参数可自定义
      const dataURL = canvas.toDataURL(`image/${ext}`)
      canvas = null
      resolve(dataURL)
    }
    img.onerror = function (err) {
      reject(err)
    }
  })
}

/**
 * 将图片转为base64
 * @param file
 */
const imageToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = function () {
      resolve(reader.result as string)
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

/**
 * 压缩图片
 * @param file
 * @param options
 * @returns
 */
const imageCompress = async (
  file: File,
  options: { quality: number; maxSize?: number; [key: string]: any } = {
    quality: 1,
  },
): Promise<File> => {
  const { maxSize } = options || {}
  const compressFile: File = await new Promise((resolve, reject) => {
    imageConversion
      .compressAccurately(file, {
        size: maxSize, // kb
      })
      .then((res: Blob) => {
        resolve(res)
      })
      .then((err: any) => {
        reject(err)
      })
  })

  return compressFile
}

/**
 * 将 HTML 元素转换为 PNG 图片。
 * @param htmlElement 要转换的 HTML 元素。
 * @returns 转换后的 PNG 图片的数据 URL，如果转换失败则返回空字符串。
 */
async function htmlElement2Png(htmlElement: HTMLElement) {
  try {
    const canvas = await html2canvas(htmlElement, {
      useCORS: true,
      allowTaint: true,
    })

    const dataURL = canvas.toDataURL('image/png')

    return dataURL
  } catch (error) {
    return ''
  }
}

export default {
  isAbsoluteUrl,
  formatImgList,
  getListSimpleUrl,
  getSimpleUrl,
  getAbsoluteUrl,
  getAbsoluteUrlByStrings,
  getVideoSnapshot,
  getVideoOriginSnapshot,
  getImageInfo,
  imgFormatter,
  getUrlBase64,
  imageToBase64,
  imageCompress,
  downloadFile,
  htmlElement2Png,
}
